import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/apiUtils';
import { useNotification } from '../components/Notification';
import { useResponsive } from './useResponsive';
import { hasTacticalAccess } from '../utils/permissions';
import {
  useMatchData,
  useDragAndDrop,
  useModalManagement,
  useMatchActions,
  useSelfReports,
  useReviews,
  useOptimisticUpdates,
  useMatchCalculations,
  usePlayerSwapping,
  useTeamSynchronization,
  useMatchUtilities,
  useMatchWebSocket
} from './useMatch';

export const useViewMatch = () => {
  const { id } = useParams();
  const { user, loginWithGoogle } = useAuth();
  const { notification, showNotification, hideNotification } = useNotification();
  const { isMobile, isCompact } = useResponsive();
  const canViewTactics = hasTacticalAccess(user?.email);

  // Match data hook
  const matchData = useMatchData(id);

  // Team synchronization hook
  const teamSync = useTeamSynchronization({
    matchId: id,
    onSetMatch: matchData.setMatch,
    onSetTeams: matchData.setTeams,
    buildTeamsFromMatch: matchData.buildTeamsFromMatch
  });

  // Player swapping hook
  const playerSwapping = usePlayerSwapping({
    matchId: id,
    onShowNotification: showNotification,
    onSyncTeams: teamSync.syncTeamsWithBackend
  });

  // Drag and drop hook
  const dragAndDrop = useDragAndDrop({
    userIsCreator: matchData.userIsCreator,
    onShowNotification: showNotification,
    onSwapPlayers: playerSwapping.swapPlayers,
    onMovePlayer: playerSwapping.movePlayer,
    onSyncTeams: teamSync.syncTeamsWithBackend
  });

  // WebSocket hook
  const webSocket = useMatchWebSocket({
    matchId: id,
    wsConnected: true, // Always try to connect
    onMatchUpdate: (match) => {
      // Handle match updates
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onTeamsUpdate: (teams) => {
      // Handle team updates
      matchData.setTeams(teams);
    },
    onPlayerAdded: (match) => {
      // Guard: si el evento WS llega con un match incompleto (sin players),
      // re-fetcheamos en vez de pisar el state con datos malformados.
      if (!match || !Array.isArray((match as any).players)) {
        matchData.fetchMatch?.();
        return;
      }
      showNotification('Un nuevo jugador se ha unido al partido', 'success');
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onPlayerRemoved: (match) => {
      showNotification('Un jugador ha abandonado el partido', 'info');
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onPlayerUpdated: (match) => {
      showNotification('Información del jugador actualizada', 'info');
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onPlayersSwapped: (match) => {
      showNotification('Jugadores intercambiados', 'info');
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onPlayerMoved: (match) => {
      showNotification('Jugador movido', 'info');
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onPlayerPromotedDemoted: (match) => {
      matchData.setMatch(match);
      matchData.buildTeamsFromMatch(match);
    },
    onSelfReportValidated: (data) => {
      showNotification('Reporte validado', 'success');
    },
    onSelfReportRejected: (data) => {
      showNotification('Reporte rechazado', 'error');
    },
    onSelfReportSubmitted: (data) => {
      showNotification('Reporte enviado', 'info');
    },
    onValidationRequestCreated: (data) => {
      showNotification('Solicitud de validación creada', 'info');
    },
    onSpaceOccupied: (spaceKey) => {
      // Handle space occupation
    },
    onSpaceReleased: (spaceKey) => {
      // Handle space release
    },
    onShowNotification: showNotification,
    onFetchMatch: matchData.fetchMatch,
    buildTeamsFromMatch: matchData.buildTeamsFromMatch
  });

  // Pending guest requests count (for organizer badge)
  const [pendingGuestRequestsCount, setPendingGuestRequestsCount] = useState(0);

  const fetchPendingGuestRequestsCount = useCallback(async () => {
    if (!matchData.userIsCreator || !id) return;
    const result = await apiRequest<any[]>(`/api/matches/${id}/guest-requests`);
    if (result.data && Array.isArray(result.data)) {
      setPendingGuestRequestsCount(result.data.filter((r: any) => r.status === 'PENDING').length);
    }
  }, [id, matchData.userIsCreator]);

  // Modal management hook
  const modals = useModalManagement();

  // Match actions hook
  const matchActions = useMatchActions({
    matchId: id,
    userIsCreator: matchData.userIsCreator,
    user,
    onShowNotification: showNotification,
    onFetchMatch: matchData.fetchMatch,
    onFetchMatchReport: matchData.fetchMatchReport,
    onSetMatch: matchData.setMatch,
    onSetUserIsCreator: matchData.setUserIsCreator,
    onSetMatchScore: matchData.setMatchScore,
    onSetReportCompleted: matchData.setReportCompleted,
    onSetMatchReport: matchData.setMatchReport,
    onSetShowJoinModal: modals.setShowJoinModal,
    onCloseJoinModal: modals.closeJoinModal,
    onOccupySpace: webSocket.occupySpace,
    onReleaseSpace: webSocket.releaseSpace,
    onSetShowEditPlayerModal: modals.setShowEditPlayerModal
  });

  // Self reports hook
  const selfReports = useSelfReports({
    matchId: id,
    match: matchData.match,
    user,
    onShowNotification: showNotification,
    onFetchMatch: matchData.fetchMatch,
    onSetShowValidationModal: modals.setShowValidationModal
  });

  // Reviews hook
  const reviews = useReviews({
    matchId: id,
    match: matchData.match,
    user,
    onShowNotification: showNotification,
    onSetShowCreateReviewModal: modals.setShowCreateReviewModal,
    onSetShowPlayerReviewsModal: modals.setShowPlayerReviewsModal,
    onSetShowPlayerProfileModal: modals.setShowPlayerProfileModal,
    onRefetchMatch: matchData.fetchMatch
  });

  // Optimistic updates hook
  const optimisticUpdates = useOptimisticUpdates({
    match: matchData.match,
    onSetMatch: matchData.setMatch
  });

  // Match calculations hook
  const calculations = useMatchCalculations({
    match: matchData.match,
    teams: matchData.teams,
    matchReport: matchData.matchReport,
    selfReports: selfReports.selfReports
  });

  // Match utilities hook
  const utilities = useMatchUtilities({
    match: matchData.match,
    user,
    onShowNotification: showNotification,
    onFetchMatch: matchData.fetchMatch,
    onSetShowImportListModal: modals.setShowImportListModal,
    onOpenJoinModal: matchActions.openJoinModal
  });

  // Get current match (with optimistic updates)
  const currentMatch = optimisticUpdates.getOptimisticMatch() || matchData.match;

  // Fetch pending guest requests count when match loads and user is organizer
  useEffect(() => {
    if (matchData.userIsCreator && matchData.match) {
      fetchPendingGuestRequestsCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchData.userIsCreator, matchData.match?.id]);

  // Abrir modal automáticamente para invitados que no están en el partido
  useEffect(() => {
    if (!user && currentMatch && !matchData.isLoadingUser) {
      // Verificar si el invitado ya se unió al partido (usando localStorage)
      const hasJoinedAsGuest = localStorage.getItem(`guest_joined_${id}`);
      if (!hasJoinedAsGuest) {
        modals.setShowJoinModal(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentMatch, matchData.isLoadingUser, id, modals.setShowJoinModal]);

  // Mostrar opciones de unión para usuarios autenticados que no están en el partido
  useEffect(() => {
    if (user && currentMatch && !matchData.isLoadingUser && !matchActions.isJoiningProcess) {
      // Verificar si el usuario ya está en el partido
      const isUserInMatch = currentMatch.players.some(p => p.userId === user.id) || 
                           currentMatch.substitutes.some(p => p.userId === user.id);

      if (!isUserInMatch && !modals.showJoinOptionsForNewUser && !modals.hasUserClosedModal) {
        // Mostrar el modal de opciones para usuarios nuevos
        modals.setShowJoinOptionsForNewUser(true);
      }
      
      // Limpiar joinMatchAfterLogin si existe
      const joinData = localStorage.getItem('joinMatchAfterLogin');
      if (joinData) {
        localStorage.removeItem('joinMatchAfterLogin');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentMatch, matchData.isLoadingUser, modals.showJoinOptionsForNewUser, modals.hasUserClosedModal, matchActions.isJoiningProcess, modals.setShowJoinOptionsForNewUser]);

  // Limpiar estado del invitado cuando el componente se desmonte o cambie de partido
  useEffect(() => {
    return () => {
      // Solo limpiar si no hay usuario (es invitado) y hay un ID de partido
      if (!user && id) {
        // No limpiar inmediatamente, solo cuando se desmonte el componente
        // Esto permite que el estado persista durante la sesión del invitado
      }
    };
  }, [user, id]);

  return {
    // Data
    match: currentMatch,
    teams: matchData.teams,
    matchReport: matchData.matchReport,
    matchScore: matchData.matchScore,
    reportCompleted: matchData.reportCompleted,
    userInfo: matchData.userInfo,
    userIsCreator: matchData.userIsCreator,
    isLoadingUser: matchData.isLoadingUser,
    
    // User and UI
    user,
    isMobile,
    isCompact,
    canViewTactics,
    
    // Notifications
    notification,
    showNotification,
    hideNotification,
    
    // Drag and Drop
    ...dragAndDrop,
    
    // Modals
    ...modals,
    
    // Match Actions
    ...matchActions,
    
    // Self Reports
    ...selfReports,
    
    // Reviews
    ...reviews,
    
    // Calculations
    ...calculations,
    
    // Utilities
    ...utilities,
    
    // WebSocket
    ...webSocket,
    
    // Optimistic Updates
    ...optimisticUpdates,
    
    // Team Synchronization
    ...teamSync,
    
    // Player Swapping
    ...playerSwapping,
    
    // Additional functions
    handleLoginClick: () => utilities.handleLoginClick(id || ''),
    handleImportList: utilities.handleImportList,
    handleImportSuccess: utilities.handleImportSuccess,
    handleJoinPosition: (teamIndex: number, positionIndex: number) => 
      utilities.handleJoinPosition(teamIndex, positionIndex, matchData.userIsCreator),
    
    // Review functions
    handleCreateReview: reviews.handleCreateReview,
    handleViewPlayerReviews: reviews.handleViewPlayerReviews,
    handleValidateStats: selfReports.handleValidateStats,
    
    // Join functions
    openJoinModal: matchActions.openJoinModal,

    // Guest requests
    pendingGuestRequestsCount,
    fetchPendingGuestRequestsCount,

    // Match data utils
    fetchMatch: matchData.fetchMatch,
  };
};